import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { getMyInfo, logoutMember } from "@/api/authApi"
// 💡 작성해 둔 LoginModal 컴포넌트를 불러옵니다. (경로는 실제 위치에 맞게 수정해주세요)
import LoginModal from "@/components/ui/LoginModal"

type User = {
    memberId: number
    loginId: string
    memberName: string
    phone: string
    email: string
    role: string
}

type AuthContextType = {
    user: User | null
    isLoggedIn: boolean
    isLoading: boolean
    refreshAuth: () => Promise<void>
    logout: () => Promise<void>
    // 💡 1. 모달 제어용 타입 추가
    isLoginModalOpen: boolean
    openLoginModal: (redirectPath?: string) => void
    closeLoginModal: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    // 💡 2. 모달 상태 및 리다이렉트 경로 상태 추가
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false)
    const [redirectPath, setRedirectPath] = useState<string | null>(null)

    // 로그인 정보 새로고침
    const refreshAuth = useCallback(async () => {
        try {
            setIsLoading(true)
            const response = await getMyInfo()

            if (response && response.data) {
                setUser(response.data)
            } else {
                setUser(null)
            }
        } catch (error) {
            console.error(error)
            setUser(null)
        } finally {
            setIsLoading(false)
        }
    }, [])

    // 로그아웃 처리
    const logout = useCallback(async () => {
        try {
            await logoutMember()
            setUser(null)
        } catch (error) {
            console.error(error)
        }
    }, [])

    // 💡 3. 모달 여닫기 함수 추가 (의존성 배열 최소화를 위해 useCallback 사용)
    const openLoginModal = useCallback((path?: string) => {
        setRedirectPath(path || null)
        setIsLoginModalOpen(true)
    }, [])

    const closeLoginModal = useCallback(() => {
        setIsLoginModalOpen(false)
        setRedirectPath(null)
    }, [])

    // 앱 실행 시 최초 1회 유저 정보 세팅
    useEffect(() => {
        refreshAuth()
    }, [refreshAuth])

    // 💡 4. useMemo 의존성 배열 및 value 객체에 모달 함수들 추가
    const value = useMemo(
      () => ({
          user,
          isLoggedIn: !!user,
          isLoading,
          refreshAuth,
          logout,
          isLoginModalOpen,
          openLoginModal,
          closeLoginModal,
      }),
      [user, isLoading, refreshAuth, logout, isLoginModalOpen, openLoginModal, closeLoginModal],
    )

    return (
      <AuthContext.Provider value={value}>
          {children}
          {/* 💡 5. 전역 LoginModal 배치 (열림 상태일 때만 렌더링) */}
          {isLoginModalOpen && <LoginModal redirectPath={redirectPath} />}
      </AuthContext.Provider>
    )
}

export function useAuth() {
    const context = useContext(AuthContext)

    if (!context) {
        throw new Error("useAuth must be used within AuthProvider")
    }

    return context
}